import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Chip,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Message,
  MoreVert,
  Refresh,
  Search,
  Send,
  Warning,
} from '@mui/icons-material';
import { useApp } from '../contexts/AppContext';
import { LaserMachine } from '../types';
import { messageService } from '../services/api';
import SurfaceCard from '../components/ui/SurfaceCard';

const typeColors: Record<string, { bg: string; border: string; icon: React.ReactNode }> = {
  info: { bg: 'rgba(79, 124, 255, 0.08)', border: 'rgba(79, 124, 255, 0.25)', icon: <Info sx={{ fontSize: 16, color: '#4F7CFF' }} /> },
  warning: { bg: 'rgba(255, 180, 84, 0.08)', border: 'rgba(255, 180, 84, 0.25)', icon: <Warning sx={{ fontSize: 16, color: '#FFB454' }} /> },
  error: { bg: 'rgba(249, 115, 115, 0.08)', border: 'rgba(249, 115, 115, 0.25)', icon: <ErrorIcon sx={{ fontSize: 16, color: '#F97373' }} /> },
  success: { bg: 'rgba(34, 197, 94, 0.08)', border: 'rgba(34, 197, 94, 0.25)', icon: <CheckCircle sx={{ fontSize: 16, color: '#22C55E' }} /> },
};

const formatRelativeTime = (timestamp: Date | string): string => {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Adesso';
  if (mins < 60) return `${mins} min fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h fa`;
  return new Date(timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const MessagesPage: React.FC = () => {
  const { state, refreshMessages } = useApp();
  const [selectedMachine, setSelectedMachine] = useState<LaserMachine | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState<'info' | 'warning' | 'error' | 'success'>('info');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Load unread counts on mount
  useEffect(() => {
    messageService.getUnreadCounts().then(setUnreadCounts).catch(() => {});
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, selectedMachine]);

  useEffect(() => {
    if (selectedMachine) {
      refreshMessages(selectedMachine.id);
    }
  }, [selectedMachine, refreshMessages]);

  const handleSendMessage = useCallback(async () => {
    if (!selectedMachine || !newMessage.trim()) return;
    try {
      setSending(true);
      await messageService.sendMessage(selectedMachine.id, newMessage.trim(), messageType);
      await refreshMessages(selectedMachine.id);
      setNewMessage('');
      setNotice(null);
    } catch {
      setNotice({ severity: 'error', message: 'Errore invio messaggio' });
    } finally {
      setSending(false);
    }
  }, [selectedMachine, newMessage, messageType, refreshMessages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMarkAsRead = async (messageId: string) => {
    try {
      await messageService.markAsRead(messageId);
      if (selectedMachine) await refreshMessages(selectedMachine.id);
    } catch {}
  };

  const filteredMachines = state.machines.filter((m) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return [m.name, m.serialNumber, m.companyName || ''].some((f) => f.toLowerCase().includes(q));
  });

  const machineMessages = selectedMachine
    ? state.messages
        .filter((msg) => msg.machineId === selectedMachine.id)
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    : [];

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="h3"
          sx={{ background: 'var(--gradient-main)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent', fontWeight: 700 }}
        >
          Messaggi HMI
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comunicazione in tempo reale con le macchine laser
        </Typography>
      </Box>

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2.5, height: { md: 'calc(100vh - 230px)' }, minHeight: 500, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Sidebar macchine */}
        <SurfaceCard
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(168, 191, 255, 0.14)' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.05)',
                px: 1,
                py: 0.4,
              }}
            >
              <Search sx={{ color: 'rgba(255,255,255,0.4)', mr: 0.6, fontSize: 18 }} />
              <InputBase
                placeholder="Cerca macchina..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: 1, color: '#fff', fontSize: '0.85rem', '& input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 } }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
            {filteredMachines.map((machine) => {
              const isSelected = selectedMachine?.id === machine.id;
              const unread = unreadCounts[machine.id] || 0;

              return (
                <Box
                  key={machine.id}
                  onClick={() => setSelectedMachine(machine)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    borderLeft: isSelected ? '3px solid var(--accent-cyan)' : '3px solid transparent',
                    backgroundColor: isSelected ? 'rgba(25, 198, 183, 0.1)' : 'transparent',
                    '&:hover': { backgroundColor: isSelected ? 'rgba(25, 198, 183, 0.14)' : 'rgba(255,255,255,0.04)' },
                    transition: 'all 0.15s',
                  }}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: machine.status === 'online' ? '#22C55E' : machine.status === 'error' ? '#EF4444' : '#6B7280',
                          border: '2px solid var(--bg-card, #111C2D)',
                        }}
                      />
                    }
                  >
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        backgroundColor: isSelected ? 'var(--accent-cyan)' : 'rgba(255,255,255,0.1)',
                        color: isSelected ? '#04111C' : 'rgba(255,255,255,0.7)',
                      }}
                    >
                      {machine.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </Badge>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }} noWrap>
                      {machine.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }} noWrap>
                      SN: {machine.serialNumber}
                      {(machine.companyName || machine.company?.name) && ` • ${machine.companyName || machine.company?.name}`}
                    </Typography>
                  </Box>

                  {unread > 0 && (
                    <Chip
                      label={unread}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                        color: '#EF4444',
                        border: '1px solid rgba(239, 68, 68, 0.35)',
                      }}
                    />
                  )}
                </Box>
              );
            })}

            {filteredMachines.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                  Nessuna macchina trovata
                </Typography>
              </Box>
            )}
          </Box>
        </SurfaceCard>

        {/* Chat area */}
        <SurfaceCard sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius: 3, overflow: 'hidden', minHeight: 0 }}>
          {selectedMachine ? (
            <>
              {/* Chat header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(168, 191, 255, 0.14)',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {selectedMachine.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    SN: {selectedMachine.serialNumber} •{' '}
                    <Box component="span" sx={{ color: selectedMachine.status === 'online' ? '#22C55E' : 'rgba(255,255,255,0.4)' }}>
                      {selectedMachine.status}
                    </Box>
                  </Typography>
                </Box>
                <Tooltip title="Aggiorna messaggi">
                  <IconButton onClick={() => refreshMessages(selectedMachine.id)} size="small">
                    <Refresh sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {machineMessages.length === 0 && (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Message sx={{ fontSize: 48, color: 'rgba(255,255,255,0.12)', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                        Nessun messaggio con questa macchina
                      </Typography>
                    </Box>
                  </Box>
                )}

                {machineMessages.map((msg) => {
                  const isOutgoing = msg.direction !== 'from_hmi';
                  const style = typeColors[msg.type] || typeColors.info;

                  return (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                        maxWidth: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '75%',
                          minWidth: 120,
                          px: 1.8,
                          py: 1,
                          borderRadius: isOutgoing ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          backgroundColor: isOutgoing ? 'rgba(25, 198, 183, 0.1)' : 'rgba(79, 124, 255, 0.08)',
                          border: `1px solid ${isOutgoing ? 'rgba(25, 198, 183, 0.22)' : 'rgba(79, 124, 255, 0.18)'}`,
                          borderLeft: !isOutgoing ? `3px solid ${style.border}` : undefined,
                          borderRight: isOutgoing ? `3px solid rgba(25, 198, 183, 0.4)` : undefined,
                          position: 'relative',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4 }}>
                          {style.icon}
                          <Chip
                            label={msg.type}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              backgroundColor: style.bg,
                              color: 'rgba(255,255,255,0.7)',
                              border: `1px solid ${style.border}`,
                            }}
                          />
                          {!msg.read && (
                            <Tooltip title="Segna come letto">
                              <IconButton size="small" onClick={() => handleMarkAsRead(msg.id)} sx={{ p: 0.2 }}>
                                <CheckCircle sx={{ fontSize: 14, color: 'var(--accent-cyan)' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: msg.read ? 400 : 600, wordBreak: 'break-word' }}>
                          {msg.message}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem', display: 'block', textAlign: isOutgoing ? 'right' : 'left', mt: 0.4 }}
                        >
                          {formatRelativeTime(msg.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}

                <div ref={chatEndRef} />
              </Box>

              {/* Send bar */}
              <Box
                sx={{
                  px: 2,
                  py: 1.2,
                  borderTop: '1px solid rgba(168, 191, 255, 0.14)',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Tooltip title={`Tipo: ${messageType}`}>
                  <IconButton size="small" onClick={(e) => setTypeMenuAnchor(e.currentTarget)}>
                    <MoreVert sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={typeMenuAnchor}
                  open={Boolean(typeMenuAnchor)}
                  onClose={() => setTypeMenuAnchor(null)}
                  slotProps={{ paper: { sx: { borderRadius: 2, background: 'rgba(12, 21, 36, 0.96)', border: '1px solid var(--border-color)' } } }}
                >
                  {(['info', 'warning', 'error', 'success'] as const).map((t) => (
                    <MenuItem
                      key={t}
                      selected={messageType === t}
                      onClick={() => {
                        setMessageType(t);
                        setTypeMenuAnchor(null);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {typeColors[t].icon}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {t === 'info' ? 'Info' : t === 'warning' ? 'Attenzione' : t === 'error' ? 'Errore' : 'Successo'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Scrivi un messaggio..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'rgba(255,255,255,0.04)',
                    },
                  }}
                />

                <IconButton
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  sx={{
                    backgroundColor: 'var(--accent-cyan)',
                    color: '#04111C',
                    width: 38,
                    height: 38,
                    '&:hover': { backgroundColor: 'rgba(25, 198, 183, 0.8)' },
                    '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <Send sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Message sx={{ fontSize: 56, color: 'rgba(255,255,255,0.1)', mb: 1.5 }} />
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                  Seleziona una macchina
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mt: 0.5 }}>
                  Scegli dalla lista per iniziare una conversazione
                </Typography>
              </Box>
            </Box>
          )}
        </SurfaceCard>
      </Box>
    </Box>
  );
};

export default MessagesPage;
